import { consoleWarnResponse } from "common/utilities/browser/consoleLog/consoleLog";
import type * as I from "core/api/coreApiClient";
import { enqueueMessage } from "core/redux/actions/notification/notificationActions";
import { openNewWindowAction, redirectAction } from "core/redux/actions/ui/uiActions";
import { getStore } from "core/redux/store/configureStore";
import type { AppStore } from "core/redux/store/configureStore.types";
import { handleAsyncException } from "core/utilities/exceptionHandler/exceptionHandler.ts";
import I18n from "i18n/en-US.ts";

import type { ResponseTypes } from "./api.typesGuards";
import { isRedirectResponse, isResponseWithType } from "./api.typesGuards";
import type { RequestContext } from "./coreApiClient";

export const NOT_FOUND = 404;
export const UNPROCESSABLE_ENTITY = 422;
export const BAD_REQUEST = 400;
export const UNAUTHORIZED = 401;
export const FORBIDDEN = 403;
export const REDIRECT = 302;
export const OK = 200;
const GATEWAY_TIMEOUT = 504;

export const StatusCode = {
  NOT_FOUND,
  UNPROCESSABLE_ENTITY,
  BAD_REQUEST,
  UNAUTHORIZED,
  FORBIDDEN,
  REDIRECT,
  OK,
};

/** Fetch request error are usually related to network issues */
export const NO_RESPONSE_MESSAGE = "Fetch request error (no response returned)";

/** Generic messages field can be returned within all responses */
export function showMessages(store: AppStore, response: I.ResponseWithMessages) {
  if (!response) {
    return;
  }
  const { messages } = response;
  if (messages && messages.length > 0) {
    store.dispatch(enqueueMessage(messages));
  }
}

export function navigate(store: AppStore, response: I.GenericResponse, responseStatus: number) {
  if (isRedirectResponse(response)) {
    const redirectResponse = response as I.RedirectResponse;

    if (redirectResponse.payload) {
      store.dispatch(redirectAction(redirectResponse.payload, responseStatus));
    }
  } else if (isResponseWithType(response)) {
    const responseWithType = response as I.GenericResponse;

    if (responseWithType.openInNewWindowUri) {
      store.dispatch(openNewWindowAction(responseWithType.openInNewWindowUri));
    }
  }
}

export function isSuccessResponse(responseStatus: number) {
  return responseStatus === 200 || responseStatus === 201;
}

/** Handled error responses by the API */
export function isHandledErrorResponse(responseStatus: number) {
  return [UNPROCESSABLE_ENTITY, BAD_REQUEST, REDIRECT].includes(responseStatus);
}

export function isUnauthorizedResponse(responseStatus: number) {
  return responseStatus === UNAUTHORIZED;
}

export function isGatewayTimeoutResponse(responseStatus: number) {
  return responseStatus === GATEWAY_TIMEOUT;
}

export function isErrorResponse(responseStatus: number) {
  return responseStatus >= 400 && responseStatus <= 599;
}

export function isResponseWithMessages(response?: ResponseTypes) {
  return response !== undefined && response.messages !== undefined && response.messages.length > 0;
}

export function isJsonResponse(response?: Response): boolean {
  if (!response) {
    return false;
  }

  const contentType = response.headers?.get("content-type");
  if (!contentType) {
    return false;
  }
  return contentType.toLowerCase().includes("application/json");
}

export function isGet(request: RequestContext) {
  const { method } = request.init;
  if (method) {
    return method.toUpperCase() === "GET";
  }

  return true;
}

/**
 * Requests aborted using the abortFetchSectionsRequests method
 */
export function isRequestAborted(requestError: unknown) {
  return ((requestError as string | undefined) || "").toString().match("The user aborted a request.") !== null;
}

/**
 * Handling of generic data that might be present in any response:
 * - show messages when present
 * - redirect when needed
 */
export function handleResponseGenericData<T>(responseData: T, responseStatus: number) {
  const store = getStore();

  if (
    isSuccessResponse(responseStatus) ||
    isErrorResponse(responseStatus) ||
    isRedirectResponse(responseData as unknown as I.GenericResponse)
  ) {
    showMessages(store, responseData as unknown as I.GenericResponse);
  }
  navigate(store, responseData as unknown as I.GenericResponse, responseStatus);
}

export async function handleResponse(url: string, response: Response) {
  // Success
  if (isSuccessResponse(response?.status)) {
    const responseData = (await response.clone().json()) as I.GenericResponse;
    handleResponseGenericData(responseData, response.status);
    return Promise.resolve(response);
  }

  if (!isJsonResponse(response)) {
    consoleWarnResponse("HANDLE RESPONSE ERROR (no-JSON):", url, response);
    handleAsyncException(response, I18n.App.ErrorBoundary.title);
    return Promise.reject(response);
  }

  consoleWarnResponse("HANDLE RESPONSE ERROR:", url, response);

  // Error
  const responseData = (await response.clone().json()) as I.GenericResponse;
  handleResponseGenericData(responseData, response.status);

  // Every other error code need to be handled by the action calling the API
  return Promise.reject(response);
}

export type CommandResponseTypes =
  | I.SectionResponse
  | I.PageResponse
  | I.ConfirmResponse
  | I.AutoCompletionResultResponse
  | I.IncidentResponse;
